/**
 * Utility functions for extracting and converting image URLs from data structures
 */

/**
 * Extracts all image URLs from a data structure
 * Handles:
 * - data.images (array)
 * - data.tenantLogo
 * - data.prospectLogo
 * - data.testimonialSection[].logo
 * @param {Object} data - The data structure
 * @returns {Array} - Array of all image URLs found
 */
export const extractImageUrls = (data) => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const imageUrls = [];

  // Extract from data.images array
  if (Array.isArray(data.images)) {
    data.images.forEach(url => {
      if (url && typeof url === 'string' && url.startsWith('http')) {
        imageUrls.push(url);
      }
    });
  }

  // Extract tenantLogo
  if (data.tenantLogo && typeof data.tenantLogo === 'string' && data.tenantLogo.startsWith('http')) {
    imageUrls.push(data.tenantLogo);
  }

  // Extract prospectLogo
  if (data.prospectLogo && typeof data.prospectLogo === 'string' && data.prospectLogo.startsWith('http')) {
    imageUrls.push(data.prospectLogo);
  }

  // Extract from testimonialSection logos
  if (Array.isArray(data.testimonialSection)) {
    data.testimonialSection.forEach(testimonial => {
      if (testimonial?.logo && typeof testimonial.logo === 'string' && testimonial.logo.startsWith('http')) {
        imageUrls.push(testimonial.logo);
      }
    });
  }

  // Remove duplicates
  return [...new Set(imageUrls)];
};

/**
 * Maps converted blob URLs back to the data structure
 * @param {Object} data - Original data structure
 * @param {Map|Object} urlMap - Map of original URLs to blob URLs (or object with URL keys)
 * @returns {Object} - Data structure with converted blob URLs
 */
export const mapBlobUrlsToData = (data, urlMap) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  // Create a map if urlMap is an object
  const urlMapping = urlMap instanceof Map 
    ? urlMap 
    : new Map(Object.entries(urlMap || {}));

  const convertedData = { ...data };

  // Convert data.images array
  if (Array.isArray(convertedData.images)) {
    convertedData.images = convertedData.images.map(url => {
      return urlMapping.get(url) || url;
    });
  }

  // Convert tenantLogo
  if (convertedData.tenantLogo && urlMapping.has(convertedData.tenantLogo)) {
    convertedData.tenantLogo = urlMapping.get(convertedData.tenantLogo);
  }

  // Convert prospectLogo
  if (convertedData.prospectLogo && urlMapping.has(convertedData.prospectLogo)) {
    convertedData.prospectLogo = urlMapping.get(convertedData.prospectLogo);
  }

  // Convert testimonialSection logos
  if (Array.isArray(convertedData.testimonialSection)) {
    convertedData.testimonialSection = convertedData.testimonialSection.map(testimonial => {
      if (testimonial?.logo && urlMapping.has(testimonial.logo)) {
        return {
          ...testimonial,
          logo: urlMapping.get(testimonial.logo)
        };
      }
      return testimonial;
    });
  }

  return convertedData;
};

/**
 * Creates a URL map from arrays of original URLs and converted blob URLs
 * @param {Array<string>} originalUrls - Array of original URLs
 * @param {Array<string>} blobUrls - Array of converted blob URLs (same order)
 * @returns {Map} - Map of original URL to blob URL
 */
export const createUrlMap = (originalUrls, blobUrls) => {
  const urlMap = new Map();
  
  if (!Array.isArray(originalUrls) || !Array.isArray(blobUrls)) {
    return urlMap;
  }

  originalUrls.forEach((originalUrl, index) => {
    const blobUrl = blobUrls[index];
    if (originalUrl && blobUrl) {
      urlMap.set(originalUrl, blobUrl);
    }
  });

  return urlMap;
};

